import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../../components/shared/Breadcrumbs'

export default function AzureAksPricingUkGuide() {
  return (
    <>
      <Helmet>
        <title>Azure AKS Pricing UK 2026 — Control Plane Traps, Spot Nodes, and Hidden Egress | AzureCalc.uk</title>
        <meta
          name="description"
          content="Azure Kubernetes Service pricing explained for UK teams. Free vs Standard vs Premium control plane, Uptime SLA double-charge, Spot node risks, and the Load Balancer egress cost that doesn't appear on the AKS pricing page. All in GBP."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/guides/azure-aks-pricing-uk" />
        <meta property="og:title" content="Azure AKS Pricing UK 2026 — Control Plane Traps, Spot Nodes, and Hidden Egress" />
        <meta property="og:url" content="https://www.azure-calc.co.uk/guides/azure-aks-pricing-uk" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <Breadcrumbs />

        <h1 className="text-2xl font-bold tracking-tight mt-4 mb-3" style={{ color: 'var(--text-primary)' }}>
          Azure AKS Pricing UK 2026
        </h1>
        <p className="text-[14px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
          Control plane tiers · Uptime SLA · Node pools · Spot nodes · Egress · Reserved Instances
        </p>
        <p className="text-[12px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
          All prices in GBP · UK South · Last verified April 2026
        </p>

        {/* Section 1 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          How AKS billing actually works
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          AKS has three distinct charging surfaces that appear on separate line items in your Azure bill:
        </p>
        <ul className="list-disc pl-5 space-y-2 mb-4 text-[14px]" style={{ color: 'var(--text-secondary)' }}>
          <li><strong>Control plane</strong> — the Kubernetes API server, etcd, and scheduling components managed by Azure. Free tier is £0; Standard tier is a flat £55.10/cluster/month regardless of node count.</li>
          <li><strong>Node pool VMs</strong> — the virtual machines running your workloads. Billed at standard VM rates (same SKUs and prices as standalone Virtual Machines in UK South). This is almost always the largest component.</li>
          <li><strong>Egress and Load Balancer</strong> — AKS provisions a Standard Load Balancer by default. Outbound rule data processing charges apply at £0.0037/GB, and these do not appear on the AKS pricing page at all.</li>
        </ul>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          For a typical production cluster with 5× D4s v3 nodes, the split looks roughly like: control plane 10%, node pool 88%, egress 2%. The node pool usually dominates — but the control plane is where the hidden surprises are.
        </p>

        {/* Section 2 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          The Standard tier upgrade trap
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          The Free tier control plane is suitable for dev and test environments. When you promote a cluster to production, the standard recommendation is to upgrade to Standard tier for the 99.5% uptime SLA on the API server.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          What teams miss: <strong>Standard tier costs £55.10/cluster/month</strong>, billed from the moment you switch — even if you have zero workloads running. For organisations running multiple environments (dev, staging, uat, prod, dr), this adds up fast:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--surface-container)' }}>
                <th className="text-left p-3 font-semibold" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>Environments</th>
                <th className="text-right p-3 font-semibold" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>Control plane only</th>
                <th className="text-right p-3 font-semibold" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>Annual</th>
              </tr>
            </thead>
            <tbody>
              {[
                { envs: '1 cluster (prod only)', monthly: '£55.10', annual: '£661' },
                { envs: '3 clusters (dev + staging + prod)', monthly: '£165.30', annual: '£1,984' },
                { envs: '5 clusters (full team)', monthly: '£275.50', annual: '£3,306' },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <td className="p-3" style={{ color: 'var(--text-secondary)' }}>{row.envs}</td>
                  <td className="p-3 text-right" style={{ color: 'var(--text-primary)' }}>{row.monthly}</td>
                  <td className="p-3 text-right" style={{ color: 'var(--text-primary)' }}>{row.annual}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          The fix is simple: keep dev and test clusters on Free tier. Only upgrade to Standard for clusters with real SLA requirements. Free tier clusters still get full Kubernetes functionality — just no guaranteed API server availability.
        </p>

        {/* Section 3 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Uptime SLA: what you're actually paying for
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Uptime SLA is a separate add-on, not a tier name. This is the source of significant confusion. The billing model is:
        </p>
        <ul className="list-disc pl-5 space-y-2 mb-4 text-[14px]" style={{ color: 'var(--text-secondary)' }}>
          <li>Standard tier: £55.10/month (flat control plane fee)</li>
          <li>Uptime SLA enabled on Standard tier: £55.10 + £55.10 = <strong>£110.20/month</strong></li>
          <li>Uptime SLA is not available on Free tier</li>
        </ul>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          The SLA uplift goes from 99.5% (Standard without Uptime SLA) to 99.95% (Standard with Uptime SLA). Whether that 0.45% availability gap is worth £55.10/month per cluster depends entirely on your workload. For most internal tooling, it isn't. For payment processing or customer-facing APIs, it often is.
        </p>

        {/* Section 4 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Node pool sizing — VM-equivalent pricing with a system overhead
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          AKS node pool VMs are billed at the same rates as standalone VMs in UK South. A D4s v3 node in an AKS cluster costs the same as a standalone D4s v3 VM: £0.1746/hour (£127.46/month) at Pay-as-you-go rates.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          What the pricing page doesn't show: every AKS node has a system node pool overhead. The kube-system components (coredns, metrics-server, konnectivity, azure-cni) consume approximately 0.5–1.0 vCPU and 1–2 GB RAM per node. On a D2s v3 (2 vCPU, 8 GB RAM), that's 25–50% of compute reserved for infrastructure before your workloads run.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Common sizing mistake: choosing D2s v3 because it costs half as much as D4s v3, then discovering that available capacity after system overhead is insufficient for the target pod density. Moving from D2s v3 to D4s v3 roughly doubles the cost but more than doubles usable capacity — the node overhead stays flat.
        </p>

        {/* Section 5 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Spot node pools — the 70% discount and when it backfires
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Spot nodes offer approximately 70% discount versus Pay-as-you-go rates. A D4s v3 drops from £0.1746/hr to roughly £0.052/hr. For a 10-node pool running 730 hours/month, that's £1,275 vs £381 — a saving worth having.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          The risk: Azure can evict Spot VMs with 30 seconds notice when capacity is needed elsewhere. Kubernetes handles this by draining the node and rescheduling pods — but only if your workloads tolerate interruption. Safe use cases for Spot nodes:
        </p>
        <ul className="list-disc pl-5 space-y-1 mb-4 text-[14px]" style={{ color: 'var(--text-secondary)' }}>
          <li>Batch processing jobs (idempotent, checkpointed)</li>
          <li>Dev and test workloads</li>
          <li>Stateless, horizontally-scalable services with multiple replicas</li>
          <li>CI/CD build agents</li>
        </ul>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          <strong>Avoid Spot for:</strong> stateful workloads (databases, message brokers), services with strict availability SLAs, anything that can't tolerate pod restart mid-operation. A common failure pattern is running a Redis cache or MongoDB replica on Spot nodes to save cost, then debugging availability incidents that trace back to node eviction.
        </p>

        {/* Section 6 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Egress and Load Balancer rule pricing
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          AKS automatically provisions a Standard Load Balancer per cluster for outbound internet access. The first 5 LB rules are included in the Standard LB base price, but outbound rule data processing charges apply: <strong>£0.0037/GB</strong>.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          This charge doesn't appear on the AKS pricing page. For clusters with low external traffic it's negligible, but for API-heavy or data-exporting workloads it can reach £30–50/month before the team notices it in the bill. Check the "Load Balancer" line in your Azure Cost Analysis, not the "Kubernetes Service" line.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          You can reduce this by using a NAT Gateway instead of the default outbound rule (NAT Gateway charges differently — see the{' '}
          <Link to="/networking" className="text-[#0078d4] hover:underline">Networking calculator</Link>
          {' '}for modelling). For clusters in the same region communicating with other Azure services, use Private Endpoints and VNet peering to avoid public egress entirely.
        </p>

        {/* Section 7 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Reserved Instances and Savings Plans for AKS nodes
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          AKS node pool VMs qualify for Reserved Instances and Azure Savings Plans — the same discounts available for standalone VMs. 1-year Reserved Instance for D4s v3 in UK South saves approximately 38% versus Pay-as-you-go; 3-year saves approximately 58%.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          The complication: AKS node pools can be resized or deleted dynamically. If you reserve capacity for a D4s v3 node pool and later resize to D8s v3, the reservation becomes stranded — you pay the reservation fee but the discount no longer applies to the new SKU. Reservations follow the VM SKU, not the AKS cluster.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Azure Savings Plans are more flexible — they apply to any compute usage across eligible SKUs. If your node pool composition is likely to change within a year, a Savings Plan carries less risk than SKU-specific reservations.
        </p>

        {/* Section 8 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Node pool sizing worked example — 100-user internal app
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          A common question on UK engineering teams: should we run 6× D2s v3 or 3× D4s v3 for a 100-user internal app? The total vCPU is identical (12 vCPU either way), but the cost and usable capacity differ significantly.
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--surface-container)' }}>
                {['', '6× D2s v3', '3× D4s v3'].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Monthly PAYG cost', a: '£764.76', b: '£382.38' },
                { label: 'Total vCPU', a: '12', b: '12' },
                { label: 'System overhead (est.)', a: '~3 vCPU (6× 0.5)', b: '~1.5 vCPU (3× 0.5)' },
                { label: 'Usable vCPU for workloads', a: '~9 vCPU', b: '~10.5 vCPU' },
                { label: 'Total RAM', a: '48 GB', b: '48 GB' },
                { label: 'Usable RAM (est.)', a: '~36 GB', b: '~42 GB' },
                { label: 'Blast radius per eviction', a: 'Low (16.7% capacity)', b: 'Higher (33.3% capacity)' },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <td className="p-3 font-medium" style={{ color: 'var(--text-secondary)' }}>{row.label}</td>
                  <td className="p-3" style={{ color: 'var(--text-primary)' }}>{row.a}</td>
                  <td className="p-3 font-semibold" style={{ color: '#107c10' }}>{row.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          <strong>3× D4s v3 wins on cost</strong> (50% cheaper) and usable capacity (fewer system overhead duplicates). The trade-off is higher blast radius per node loss: losing one of three nodes drops 33% of capacity vs 17% for the six-node pool. For a 100-user internal app that isn't latency-critical, this is acceptable. Add a fourth D4s v3 as a buffer node (£509.84/month total) and you get both the cost efficiency and the blast-radius safety net.
        </p>
        <p className="text-[14px] mb-4" style={{ color: 'var(--text-secondary)' }}>
          For workloads requiring strict pod anti-affinity (e.g. one replica per node for HA), more smaller nodes gives more placement flexibility. For bulk throughput workloads (batch, ML inference), fewer larger nodes reduce scheduling overhead and network hops.
        </p>
        <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <p className="text-[12px] font-mono uppercase tracking-widest mb-2" style={{ color: 'var(--text-tertiary)' }}>Reservation saving on this cluster</p>
          <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            3× D4s v3 at PAYG: <strong>£382.38/month</strong>. With 1-year Reserved Instances (~38% off): <strong>≈£237/month</strong>. Annual saving: ≈£1,738. Reservations follow the VM SKU — if you resize the node pool, evaluate whether the existing reservation still applies before committing.
          </p>
        </div>

        {/* Section 9 */}
        <h2 className="text-[18px] font-bold mt-8 mb-3" style={{ color: 'var(--text-primary)' }}>
          Monitoring AKS with Log Analytics — the Container Insights cost
        </h2>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Azure Monitor Container Insights is the recommended monitoring solution for AKS. It works by installing the Azure Monitor agent on each node, which collects metrics, logs, and container inventory and ships them to a Log Analytics workspace. It is one of the most common sources of unexpectedly large Log Analytics bills.
        </p>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Container Insights ingestion typically generates <strong>2–5 GB per node per day</strong>, depending on cluster activity, the number of containers per node, and log verbosity. For a 5-node cluster:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--surface-container)' }}>
                {['Scenario', 'GB/day', '£/month (PAYG)', 'Notes'].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold" style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--outline-variant)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { scenario: 'Quiet cluster', gb: '~10 GB', cost: '≈£11/mo', note: '2 GB/node/day, 5 nodes — mostly within 5 GB/day free allowance' },
                { scenario: 'Active production', gb: '~25 GB', cost: '≈£55/mo', note: '5 GB/node/day, 5 nodes — billing mostly above free tier' },
                { scenario: 'High-verbosity logging', gb: '~50 GB', cost: '≈£109/mo', note: 'Noisy apps or stdout-heavy containers' },
              ].map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                  <td className="p-3" style={{ color: 'var(--text-secondary)' }}>{row.scenario}</td>
                  <td className="p-3 font-mono" style={{ color: 'var(--text-primary)' }}>{row.gb}</td>
                  <td className="p-3 font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{row.cost}</td>
                  <td className="p-3 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[14px] mb-3" style={{ color: 'var(--text-secondary)' }}>
          Container Insights ingestion is the single largest variable cost in many AKS deployments — larger than the control plane fee for clusters with verbose workloads. Three levers to reduce it:
        </p>
        <ul className="list-disc pl-5 space-y-2 mb-4 text-[14px]" style={{ color: 'var(--text-secondary)' }}>
          <li><strong>Reduce collection frequency</strong> — the default metrics scrape interval is 60 seconds. Increasing to 120s halves metric volume with minimal monitoring impact for non-latency-critical clusters.</li>
          <li><strong>Exclude noisy namespaces</strong> — kube-system and the Azure CNI components generate significant log volume. Exclude them from collection if you don't need them in your workspace.</li>
          <li><strong>Use Basic Logs tier for Container Insights tables</strong> — Log Analytics Basic tier (£0.524/GB ingestion, 8-day retention) is available for ContainerLog and ContainerInsights tables. This is appropriate if you query these logs rarely and primarily need them for incident response.</li>
        </ul>
        <p className="text-[14px] mb-4" style={{ color: 'var(--text-secondary)' }}>
          Use the{' '}
          <Link to="/log-analytics" className="text-[#0078d4] hover:underline">Log Analytics calculator</Link>
          {' '}to model Container Insights ingestion costs before enabling it on a new cluster — especially before enabling it on a cluster with high pod density or verbose logging.
        </p>

        {/* FAQ */}
        <h2 className="text-[18px] font-bold mt-8 mb-4" style={{ color: 'var(--text-primary)' }}>
          Frequently asked questions
        </h2>
        <div className="space-y-5">
          {[
            {
              q: 'Is the AKS control plane free?',
              a: 'Only on the Free tier. Standard tier costs £55.10/cluster/month and Premium costs £330.60/cluster/month. Free tier is full-featured Kubernetes — the difference is the guaranteed SLA on the API server.',
            },
            {
              q: 'What is the Uptime SLA fee on top of Standard tier?',
              a: 'Uptime SLA is an add-on charged at £55.10/cluster/month in addition to the Standard control plane fee. Standard + Uptime SLA = £110.20/month per cluster. It upgrades the API server availability SLA from 99.5% to 99.95%.',
            },
            {
              q: 'Why does the AKS pricing page not mention Load Balancer egress?',
              a: "It's billed under the Load Balancer service, not the Kubernetes Service. Outbound rule data processing charges at £0.0037/GB. Check your Azure Cost Analysis under Load Balancer to find it.",
            },
            {
              q: 'Can I use Reserved Instances for AKS nodes?',
              a: 'Yes — AKS node pool VMs qualify for Reserved Instances and Azure Savings Plans. The reservation follows the VM SKU, so be careful when resizing node pools — stranded reservations are a common waste pattern.',
            },
            {
              q: 'Is Spot safe for production workloads?',
              a: 'Only for stateless, interruption-tolerant workloads with multiple replicas. Do not use Spot for databases, message brokers, stateful services, or anything with a hard availability requirement. Node eviction can happen with 30 seconds notice.',
            },
          ].map(({ q, a }) => (
            <div key={q} className="pb-4" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
              <p className="text-[14px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{q}</p>
              <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>{a}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 pt-6" style={{ borderTop: '1px solid var(--outline-variant)' }}>
          <Link
            to="/aks"
            className="flex items-center gap-3 p-4 rounded transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
          >
            <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>calculate</span>
            <div>
              <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>AKS Cost Calculator</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Model control plane tiers, node pools, Uptime SLA, Spot nodes, and LB egress in GBP</p>
            </div>
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
          </Link>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/virtual-machines"
            className="flex items-center gap-2 p-3 rounded text-[13px] transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
          >
            <span className="material-symbols-outlined text-[16px]" style={{ color: '#0078d4' }}>computer</span>
            <span className="group-hover:text-[#0078d4] transition-colors">VM Calculator — same SKU rates as AKS nodes</span>
          </Link>
          <Link
            to="/bandwidth"
            className="flex items-center gap-2 p-3 rounded text-[13px] transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
          >
            <span className="material-symbols-outlined text-[16px]" style={{ color: '#0078d4' }}>swap_horiz</span>
            <span className="group-hover:text-[#0078d4] transition-colors">Bandwidth Calculator — model cluster egress costs</span>
          </Link>
          <Link
            to="/log-analytics"
            className="flex items-center gap-2 p-3 rounded text-[13px] transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
          >
            <span className="material-symbols-outlined text-[16px]" style={{ color: '#0078d4' }}>monitor_heart</span>
            <span className="group-hover:text-[#0078d4] transition-colors">Log Analytics — Container Insights ingestion cost</span>
          </Link>
          <Link
            to="/managed-disks"
            className="flex items-center gap-2 p-3 rounded text-[13px] transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
          >
            <span className="material-symbols-outlined text-[16px]" style={{ color: '#0078d4' }}>storage</span>
            <span className="group-hover:text-[#0078d4] transition-colors">Managed Disks — persistent volume storage costs</span>
          </Link>
          <Link
            to="/guides/azure-billing-traps-uk"
            className="flex items-center gap-2 p-3 rounded text-[13px] transition-colors group"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
          >
            <span className="material-symbols-outlined text-[16px]" style={{ color: '#e8b000' }}>warning</span>
            <span className="group-hover:text-[#0078d4] transition-colors">Azure Billing Traps — more hidden cost patterns</span>
          </Link>
        </div>
      </div>
    </>
  )
}
